
// Fallbacks for the count-dependent copy. The section passes merchant-edited
// templates via data-copy; anything blank or missing falls back to these, so
// the shipped wording survives an empty setting or an older section JSON.
const DEFAULT_COPY = {
  status_empty: "Add {min} meals to start your box",
  status_need: "Add {need} more meal{s} to start your box",
  status_progress: "{count} meal{s} added — add {remaining} more to save more",
  status_full: "Your box is full!",
  countmax: "{count} of {max} meals added",
  per_meal: "{price} per meal",
  saving: "You save {saving} vs a {min}-meal box",
  saving_at_min: "Add more meals to lower the price",
  saving_teaser: "Prices from {price}/meal",
  hint_empty: "Add {min} meals to start your box",
  hint_need: "Add {need} more meal{s} to checkout",
  points: "🌿 {points} plant points earned",
  best_price: "Add {remaining} more for best price",
  best_price_unlocked: "Best price unlocked!",
  stats: "🌿 {points} plant points · {best_price}",
};

if (!customElements.get("box-builder")) {
  customElements.define(
    "box-builder",
    class BoxBuilder extends HTMLElement {
      constructor() {
        super();
        this._onBoxUpdated = this._onBoxUpdated.bind(this);
        this._onClick = this._onClick.bind(this);
        this._onDeliveryTimeChanged = this._onDeliveryTimeChanged.bind(this);
      }

      connectedCallback() {
        this.min = parseInt(this.dataset.min, 10) || 4;
        this.max = parseInt(this.dataset.max, 10) || 8;
        this.boxKey = this.dataset.boxKey || "build-a-box";

        try {
          this.tiers = JSON.parse(this.dataset.tiers || "{}");
        } catch (e) {
          this.tiers = {};
        }

        try {
          this.copy = JSON.parse(this.dataset.copy || "{}");
        } catch (e) {
          this.copy = {};
        }
        // Baseline = configured base price, else the most expensive tier (at min).
        this.basePrice =
          parseInt(this.dataset.basePrice, 10) || this._tierPrice(this.min) || 0;
        // Lowest achievable per-meal price (the biggest box) — for the teaser copy.
        this.lowestPrice = this._tierPrice(this.max);

        // Cache panel elements. Hooks that exist in BOTH the desktop sidebar and
        // the mobile order bar are NodeLists so one render pass keeps both in sync.
        this.els = {
          slots: this.querySelector("[data-box-slots]"), // desktop image slots
          progress: this.querySelector("[data-box-progress]"), // mobile segments
          status: this.querySelectorAll("[data-box-status]"), // desktop status copy
          countMax: this.querySelectorAll("[data-box-countmax]"), // mobile "N of MAX"
          total: this.querySelectorAll("[data-box-total]"),
          crossed: this.querySelectorAll("[data-box-crossed]"),
          perMeal: this.querySelectorAll("[data-box-per-meal]"),
          saving: this.querySelectorAll("[data-box-saving]"), // desktop saving copy
          stats: this.querySelectorAll("[data-box-stats]"), // mobile points + best-price
          delivery: this.querySelectorAll("[data-box-delivery]"),
          checkout: this.querySelectorAll("[data-box-checkout]"),
          hint: this.querySelectorAll("[data-box-hint]"), // desktop checkout hint
          points: this.querySelectorAll("[data-box-points]"), // desktop points line
        };

        // Map variant id -> { image, points } from the rendered cards.
        this.cards = {};
        this.querySelectorAll("[data-box-meal]").forEach((card) => {
          this.cards[card.dataset.variantId] = {
            el: card,
            image: card.dataset.image,
            name: card.dataset.name,
            price: parseInt(card.dataset.price, 10) || 0,
            points: parseInt(card.dataset.plantPoints, 10) || 0,
          };
        });

        this._deliveryDatesRow = this.querySelector(".build-a-box__dates-row");
        this._initialDeliveryDates = this.dataset.deliveryDates || "";
        this._initialDeliveryDatesMarkup = this._deliveryDatesRow?.innerHTML || "";
        this._deliveryCutoffChangePending = false;

        this.addEventListener("click", this._onClick);
        document.addEventListener("box:updated", this._onBoxUpdated);
        document.addEventListener("delivery:time-changed", this._onDeliveryTimeChanged);

        this._applyDeliveryCutoff();
        this._deliveryCutoffTimer = window.setInterval(() => this._applyDeliveryCutoff(), 60 * 1000);
        this._init();
        this._syncDeliveryDates();
      }

      // Liquid generates the first list, but a cached page can remain open
      // across Saturday 19:00. Remove the newly unavailable Monday in the
      // browser too, using the store timezone rather than the customer's.
      _applyDeliveryCutoff() {
        const cutoff = window.allplants && window.allplants.deliveryCutoff;
        if (!cutoff) return;

        if (!cutoff.isPassed(this.dataset.deliveryTimezone)) {
          this._deliveryCutoffChangePending = false;
          this._deliveryCutoffReplacementLabel = "";
          if (this.dataset.deliveryDates !== this._initialDeliveryDates) {
            this.dataset.deliveryDates = this._initialDeliveryDates;
            if (this._deliveryDatesRow) {
              this._deliveryDatesRow.innerHTML = this._initialDeliveryDatesMarkup;
            }
            this._initDate();
          }
          return;
        }

        const monday = cutoff.upcomingMondayIso(this.dataset.deliveryTimezone);
        const entries = (this.dataset.deliveryDates || "").split("||").filter(Boolean);
        const available = entries.filter((entry) => entry.split("::")[0] !== monday);
        if (available.length === entries.length) return;

        const active = this.querySelector("[data-box-date].is-active");
        const stored = this._box()?.state(this.boxKey).attributes?.["Delivery Date"];
        const wasSelected = (active?.dataset.boxDate || stored) === monday;
        const replacement = cutoff.nextAvailableAfterCutoff(this.dataset.deliveryTimezone, available);
        this._deliveryCutoffChangePending = wasSelected;
        this.dataset.deliveryDates = available.join("||");
        this.querySelectorAll("[data-box-date]").forEach((button) => {
          if (button.dataset.boxDate === monday) button.remove();
        });

        const buttons = Array.from(this.querySelectorAll("[data-box-date]"));
        const selected = wasSelected
          ? buttons.find((button) => button.dataset.boxDate === replacement?.iso) || buttons[0]
          : buttons.find((button) => button.classList.contains("is-active")) || buttons[0];
        this._deliveryCutoffReplacementLabel = wasSelected
          ? selected?.textContent.trim() || replacement?.label || "the next available date"
          : "";
        buttons.forEach((button) => {
          const active = button === selected;
          button.classList.toggle("is-active", active);
          button.setAttribute("aria-pressed", active ? "true" : "false");
        });

        const box = this._box();
        if (box && selected) {
          const selectedDate = selected.dataset.boxDate || selected.textContent.trim();
          box.setAttribute(this.boxKey, "Delivery Date", selectedDate);
        }
      }

      // Hand the currently-rendered date list to the cart on EVERY visit, not
      // just at checkout. The cart can't read this section's blocks, so it
      // mirrors this attribute — and refreshed only at checkout it goes stale
      // the moment a merchant edits or skips a date, which is what left the
      // cart offering dates the picker no longer showed.
      //
      // Posts straight to /cart/update.js rather than via window.allplants.cart:
      // that object comes from component-cart.js, which only the cart section
      // loads, so on this page it is undefined and the write silently never
      // happened. _checkout() below posts the same way for the same reason.
      async _syncDeliveryDates() {
        const dates = this.dataset.deliveryDates;
        if (!dates) return;
        try {
          await fetch(this._root() + "/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ attributes: { _delivery_dates: dates } }),
          });
        } catch (err) {
          // Non-fatal: the cart keeps the previous list rather than breaking the
          // picker. _checkout() writes it again anyway.
          console.error("[box-builder] delivery-date sync failed", err);
        }
      }

      disconnectedCallback() {
        this.removeEventListener("click", this._onClick);
        document.removeEventListener("box:updated", this._onBoxUpdated);
        document.removeEventListener("delivery:time-changed", this._onDeliveryTimeChanged);
        window.clearInterval(this._deliveryCutoffTimer);
      }

      _onDeliveryTimeChanged() {
        this._applyDeliveryCutoff();
      }

      // ── box state (localStorage, not the cart) ───────────────
      _box() {
        return window.allplants && window.allplants.box;
      }

      // The box selection as cart-line-item-shaped objects.
      _boxItems() {
        const box = this._box();
        return box ? box.items(this.boxKey) : [];
      }

      _count() {
        return this._boxItems().reduce((s, it) => s + it.quantity, 0);
      }

      _qtyFor(boxItems, variantId) {
        const item = boxItems.find(
          (it) => String(it.variant_id || it.id) === String(variantId),
        );
        return item ? item.quantity : 0;
      }

      // On load, if the box is empty, rehydrate it from any box lines already in
      // the cart — so "back to edit" from the cart shows the meals again.
      async _init() {
        const box = this._box();
        if (box && box.count(this.boxKey) === 0) {
          await this._seedFromCart();
        }
        this._initDate();
        this._renderFromStore();
      }

      // Keep the delivery date the customer *sees* selected in sync with the box
      // store on load. Honour a date already saved (activating its button);
      // otherwise adopt whichever date button is active by default (the first)
      // and record it — so checkout always sends the date shown in the section.
      _initDate() {
        const box = this._box();
        if (!box) return;
        const dateBtns = Array.from(this.querySelectorAll("[data-box-date]"));
        if (!dateBtns.length) return;

        // data-box-date is the ISO value (2026-07-20); the button's text is the
        // human label. The cart attribute stores the ISO, so match on that.
        const valueOf = (b) => b.dataset.boxDate || b.textContent.trim();
        const stored = (box.state(this.boxKey).attributes || {})["Delivery Date"];

        let selected = stored
          ? dateBtns.find((b) => valueOf(b) === stored) || null
          : null;
        if (!selected) {
          // No saved date (or it's no longer offered): use the active/first button.
          selected =
            dateBtns.find((b) => b.classList.contains("is-active")) || dateBtns[0];
          box.setAttribute(this.boxKey, "Delivery Date", valueOf(selected));
        }

        dateBtns.forEach((b) => {
          const on = b === selected;
          b.classList.toggle("is-active", on);
          b.setAttribute("aria-pressed", on ? "true" : "false");
        });
      }

      async _seedFromCart() {
        const box = this._box();
        if (!box) return;
        try {
          const base = this._root();
          const res = await fetch(base + "/cart.js", { headers: { Accept: "application/json" } });
          if (!res.ok) return;
          const cart = await res.json();
          (cart.items || [])
            .filter((it) => it.properties && it.properties._box === this.boxKey)
            .forEach((it) => {
              const card = this.cards[String(it.variant_id || it.id)];
              box.change(this.boxKey, it.variant_id || it.id, it.quantity, {
                image: (card && card.image) || it.image || (it.featured_image && it.featured_image.url) || "",
                title: (card && card.name) || it.title || "",
                price: (card && card.price) || it.price || 0,
                points: card ? card.points : 0,
              });
            });
          const date = cart.attributes && cart.attributes["Delivery Date"];
          if (date) box.setAttribute(this.boxKey, "Delivery Date", date);
        } catch (e) {
          /* offline / editor — just render the empty box */
        }
      }

      _renderFromStore() {
        this._render(this._boxItems());
      }

      _root() {
        const root =
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        return root.replace(/\/$/, "");
      }

      // ── events ───────────────────────────────────────────────
      _onBoxUpdated(e) {
        // Ignore updates for a different box on the same page.
        if (e.detail && e.detail.boxKey && e.detail.boxKey !== this.boxKey) return;
        this._renderFromStore();
      }

      _onClick(e) {
        const box = this._box();

        const addBtn = e.target.closest("[data-box-add]");
        const stepBtn = e.target.closest("[data-box-step]");
        const dateBtn = e.target.closest("[data-box-date]");
        const checkoutBtn = e.target.closest("[data-box-checkout]");

        if (checkoutBtn) {
          if (!checkoutBtn.disabled) this._checkout();
          return;
        }
        if (dateBtn) {
          this._selectDate(dateBtn);
          return;
        }
        if (!addBtn && !stepBtn) return;
        if (!box) return;

        const variantId = (addBtn || stepBtn).dataset.variantId;
        const card = this.cards[variantId];
        if (!card) return;

        const count = this._count();

        // Relative deltas so rapid clicks can't lose updates. The store persists
        // the selection; nothing touches the Shopify cart until checkout.
        if (addBtn || (stepBtn && stepBtn.dataset.boxStep === "increase")) {
          if (count >= this.max) return; // box full — ignore
          box.change(this.boxKey, Number(variantId), 1, {
            image: card.image,
            title: card.name,
            price: card.price,
            points: card.points,
          });
        } else if (stepBtn && stepBtn.dataset.boxStep === "decrease") {
          box.change(this.boxKey, Number(variantId), -1);
        }
      }

      // Flush the whole box into the Shopify cart in one request, carry the
      // chosen delivery date over as a cart attribute, then go to the cart.
      // Idempotent: existing box lines are cleared first, so re-checkout after
      // "back to edit" replaces rather than duplicates.
      async _checkout() {
        const box = this._box();
        if (!box) return;

        // The interval is only a UI refresh. Recheck at the exact click so a
        // customer cannot carry the Monday date across the 7pm boundary
        // between timer ticks.
        this._applyDeliveryCutoff();
        if (this._deliveryCutoffChangePending) {
          this._deliveryCutoffChangePending = false;
          window.alert(
            `Delivery cut-off passed. Date changed to ${this._deliveryCutoffReplacementLabel || "the next available date"}.`,
          );
          return;
        }
        const items = this._boxItems();
        if (!items.length) return;

        this.els.checkout.forEach((b) => (b.disabled = true));

        const base = this._root();
        const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" };

        try {
          // Remove any box lines already in the cart (from a previous checkout).
          const cart = await fetch(base + "/cart.js", { headers: { Accept: "application/json" } }).then((r) => r.json());
          const updates = {};
          (cart.items || []).forEach((it) => {
            if (it.properties && it.properties._box === this.boxKey) updates[it.key] = 0;
          });
          if (Object.keys(updates).length) {
            await fetch(base + "/cart/update.js", {
              method: "POST",
              headers: JSON_HEADERS,
              body: JSON.stringify({ updates }),
            });
          }

          // Add the current box selection.
          const res = await fetch(base + "/cart/add.js", {
            method: "POST",
            headers: JSON_HEADERS,
            body: JSON.stringify({
              items: items.map((it) => ({
                id: it.variant_id,
                quantity: it.quantity,
                properties: { _box: this.boxKey },
              })),
            }),
          });
          if (!res.ok) throw new Error("cart/add failed: " + res.status);

          const attributes = {};
          const date = (box.state(this.boxKey).attributes || {})["Delivery Date"];
          if (date) attributes["Delivery Date"] = date;
          if (this.dataset.deliveryDates) {
            attributes["_delivery_dates"] = this.dataset.deliveryDates;
          }
          if (Object.keys(attributes).length) {
            await fetch(base + "/cart/update.js", {
              method: "POST",
              headers: JSON_HEADERS,
              body: JSON.stringify({ attributes }),
            });
          }

          window.location.href = base + "/cart";
        } catch (err) {
          console.error("[box-builder] checkout failed", err);
          this.els.checkout.forEach((b) => (b.disabled = false));
        }
      }

      _selectDate(btn) {
        this.querySelectorAll("[data-box-date]").forEach((b) => {
          b.classList.toggle("is-active", b === btn);
          b.setAttribute("aria-pressed", b === btn ? "true" : "false");
        });
        const value = btn.dataset.boxDate || btn.textContent.trim();
        // Held on the box until checkout, then sent as a cart attribute (ISO).
        const box = this._box();
        if (box) box.setAttribute(this.boxKey, "Delivery Date", value);
      }

      // Resolve one piece of count-dependent copy: take the merchant's template
      // (or the built-in default) and substitute {token}s. An unknown token is
      // left as-is rather than blanked, so a typo in the customiser is visible
      // instead of silently eating text.
      _copy(key, tokens) {
        const custom = this.copy[key];
        const template =
          typeof custom === "string" && custom.trim() !== ""
            ? custom
            : DEFAULT_COPY[key] || "";
        return template.replace(/\{(\w+)\}/g, (match, name) =>
          Object.prototype.hasOwnProperty.call(tokens, name)
            ? tokens[name]
            : match,
        );
      }

      // Plural suffix for {s}: empty only when the governing number is exactly 1.
      _s(n) {
        return n === 1 ? "" : "s";
      }

      // Set textContent on every element in a NodeList (sidebar + order bar).
      _setAll(list, text) {
        if (!list) return;
        list.forEach((el) => {
          el.textContent = text;
        });
      }

      // ── render ───────────────────────────────────────────────
      _render(boxItems) {
        const count = boxItems.reduce((s, it) => s + it.quantity, 0);

        this._renderCards(boxItems);
        this._renderSlots(boxItems, count);
        this._renderProgress(count);
        this._renderStatus(count);
        this._renderPricing(count);
        this._renderDelivery(count);
        this._renderCheckout(count);
        this._renderPoints(boxItems, count);
      }

      // Mobile order-bar progress: `max` segments, filled up to `count`.
      _renderProgress(count) {
        if (!this.els.progress) return;
        let html = "";
        for (let i = 0; i < this.max; i++) {
          const filled = i < count;
          html +=
            '<div class="box-builder__seg' +
            (filled ? " is-filled" : "") +
            '"></div>';
        }
        this.els.progress.innerHTML = html;
      }

      _renderCards(boxItems) {
        const atMax =
          boxItems.reduce((s, it) => s + it.quantity, 0) >= this.max;
        Object.keys(this.cards).forEach((variantId) => {
          const card = this.cards[variantId];
          const qty = this._qtyFor(boxItems, variantId);
          const addBtn = card.el.querySelector("[data-box-add]");
          const qtyCtrl = card.el.querySelector("[data-box-qty]");
          const qtyNum = card.el.querySelector("[data-box-qty-num]");
          // Sold-out cards do not have an add button, but they still render a
          // quantity control so an item already in the box can be removed.
          if (!qtyCtrl) return;

          if (qty > 0) {
            addBtn?.classList.add("hidden");
            qtyCtrl.classList.remove("hidden");
            qtyCtrl.classList.add("flex");
            if (qtyNum) qtyNum.textContent = qty;
            const plus = qtyCtrl.querySelector('[data-box-step="increase"]');
            if (plus) plus.disabled = atMax || card.el.classList.contains("is-unavailable");
          } else {
            addBtn?.classList.remove("hidden");
            qtyCtrl.classList.add("hidden");
            qtyCtrl.classList.remove("flex");
            // Disable adding new meals when the box is full.
            if (addBtn) {
              addBtn.disabled = atMax;
              addBtn.classList.toggle("opacity-40", atMax);
              addBtn.classList.toggle("cursor-not-allowed", atMax);
            }
          }
        });
      }

_renderSlots(boxItems, count) {
  if (!this.els.slots) return;

  // Build one entry per unit: { img, variantId }
  const thumbs = [];
  for (let n = boxItems.length - 1; n >= 0; n--) {
    const it = boxItems[n];
    const card = this.cards[String(it.variant_id || it.id)];
    const img = (card && card.image) || it.image || "";
    for (let i = 0; i < it.quantity; i++) {
      thumbs.push({ img, variantId: String(it.variant_id || it.id) });
    }
  }

  let html = "";
  for (let i = 0; i < this.max; i++) {
    if (thumbs[i]) {
      html +=
        '<div class="box-builder__slot box-builder__slot--filled is-filled" style="position:relative;">' +
        '<img src="' + thumbs[i].img + '" alt="" loading="lazy">' +
        '<button class="box-builder__slot-remove" data-remove-variant="' + thumbs[i].variantId + '" ' +
        'aria-label="Remove meal" style="position:absolute;top:3px;right:3px;width:18px;height:18px;' +
        'border-radius:50%;background:rgba(255,255,255,0.85);border:none;cursor:pointer;' +
        'font-size:12px;line-height:1;display:flex;align-items:center;justify-content:center;' +
        'color:#3a4a3a;padding:0;">×</button>' +
        '</div>';
    } else {
      html += '<div class="box-builder__slot" aria-hidden="true">+</div>';
    }
  }
  this.els.slots.innerHTML = html;

  // Wire up remove buttons
  this.els.slots.querySelectorAll("[data-remove-variant]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const variantId = btn.dataset.removeVariant;
      const card = this.cards[variantId];
      if (!card) return;
      const decreaseBtn = card.el.querySelector('[data-box-step="decrease"]');
      if (decreaseBtn) decreaseBtn.click();
    });
  });
}


      _renderStatus(count) {
        // Desktop status copy.
        const base = { count: count, min: this.min, max: this.max };
        let text;
        if (count === 0) {
          text = this._copy("status_empty", { ...base, s: this._s(this.min) });
        } else if (count < this.min) {
          const need = this.min - count;
          text = this._copy("status_need", { ...base, need: need, s: this._s(need) });
        } else if (count >= this.max) {
          text = this._copy("status_full", { ...base, s: this._s(count) });
        } else {
          text = this._copy("status_progress", {
            ...base,
            remaining: this.max - count,
            s: this._s(count),
          });
        }
        this._setAll(this.els.status, text);

        // Mobile "N of MAX meals added".
        this._setAll(
          this.els.countMax,
          this._copy("countmax", {
            ...base,
            remaining: this.max - count,
            s: this._s(count),
          }),
        );
      }

      _renderPricing(count) {
        const { total, crossed, perMeal, saving } = this.els;

        const base = { count: count, min: this.min, max: this.max, s: this._s(count) };

        if (count >= this.min) {
          const ppm = this._tierPrice(count);
          this._setAll(total, this._money(ppm * count));
          this._setAll(perMeal, this._copy("per_meal", { ...base, price: this._money(ppm) }));

          if (count > this.min) {
            this._setAll(crossed, this._money(this.basePrice * count));
            this._setAll(
              saving,
              this._copy("saving", {
                ...base,
                price: this._money(ppm),
                saving: this._money((this.basePrice - ppm) * count),
              }),
            );
          } else {
            this._setAll(crossed, "");
            this._setAll(saving, this._copy("saving_at_min", base));
          }
        } else if (count > 0) {
          this._setAll(total, "—");
          this._setAll(crossed, "");
          this._setAll(perMeal, "");
          this._setAll(
            saving,
            this._copy("saving_teaser", { ...base, price: this._money(this.lowestPrice) }),
          );
        } else {
          this._setAll(total, "—");
          this._setAll(crossed, "");
          this._setAll(perMeal, "");
          this._setAll(saving, "");
        }
      }

      _renderDelivery(count) {
        if (!this.els.delivery) return;
        const visible = count >= this.min;
        this.els.delivery.forEach((el) => el.classList.toggle("is-visible", visible));
      }

      _renderCheckout(count) {
        const { checkout, hint } = this.els;
        const ready = count >= this.min;
        if (checkout) checkout.forEach((btn) => (btn.disabled = !ready));

        let hintText = "";
        if (!ready) {
          const base = { count: count, min: this.min, max: this.max };
          if (count === 0) {
            hintText = this._copy("hint_empty", { ...base, s: this._s(this.min) });
          } else {
            const need = this.min - count;
            hintText = this._copy("hint_need", { ...base, need: need, s: this._s(need) });
          }
        }
        this._setAll(hint, hintText);
      }

      _renderPoints(boxItems, count) {
        const pts = boxItems.reduce((s, it) => {
          const card = this.cards[String(it.variant_id || it.id)];
          // Prefer the grid card's points; fall back to points stored on the
          // box item (for meals added from the scroller, not shown in this grid).
          const p = card ? card.points : it.points || 0;
          return s + p * it.quantity;
        }, 0);

        const more = this.max - count;
        const base = { count: count, min: this.min, max: this.max, remaining: more, points: pts };

        // Desktop points line.
        this._setAll(
          this.els.points,
          pts > 0 ? this._copy("points", { ...base, s: this._s(pts) }) : "",
        );

        // Mobile stats line: points + best-price teaser.
        const bestPrice =
          more > 0
            ? this._copy("best_price", { ...base, s: this._s(more) })
            : this._copy("best_price_unlocked", { ...base, s: this._s(more) });
        const stats =
          pts > 0
            ? this._copy("stats", { ...base, best_price: bestPrice, s: this._s(pts) })
            : bestPrice;
        this._setAll(this.els.stats, stats);
      }

      // ── helpers ──────────────────────────────────────────────
      _tierPrice(count) {
        // Clamp to the configured range, then look up the per-meal price.
        const c = Math.max(this.min, Math.min(this.max, count));
        const price = this.tiers[c] || this.tiers[String(c)];
        return typeof price === "number" ? price : 0;
      }

      _money(cents) {
        if (window.Shopify && typeof Shopify.formatMoney === "function") {
          return Shopify.formatMoney(cents);
        }
        return "£" + (cents / 100).toFixed(2);
      }
    },
  );
}
